/**
 * LLM tasks workspace (Brief 185) — the live task catalog (from the registry)
 * plus the customer's own call receipts (the activity stream filtered to llm,
 * each row expandable to the cost-plus breakdown).
 */
import { useQuery } from '@tanstack/react-query';
import { Alert, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ActivityStream } from '@/components/ActivityStream';
import { fetchLlmTasks, llmTasksKey } from '@/lib/queries';
import { ApiError } from '@/lib/fetcher';

export function LlmWorkspace() {
  const q = useQuery({ queryKey: llmTasksKey, queryFn: fetchLlmTasks });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">LLM tasks</h1>
        <p className="text-muted-foreground">
          Pick a task and a tier; you pay the actual provider cost plus a small
          markup. POST /v1/llm/&#123;task&#125; from any agent or key.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Task catalog</CardTitle>
          <CardDescription>
            {q.data
              ? `${q.data.tasks.length} tasks · cost + ${q.data.pricing.markupPct}% · live from the registry`
              : 'The live registry of available tasks.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {q.isPending ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : q.error ? (
            <Alert variant="destructive">
              <AlertTitle>
                Couldn't load the catalog ({q.error instanceof ApiError ? q.error.code : 'unknown'})
              </AlertTitle>
            </Alert>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {q.data?.tasks.map((t) => (
                <div key={t.key} className="rounded-md border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{t.label}</span>
                    <code className="text-xs text-muted-foreground">{t.key}</code>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{t.description}</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {t.tiers.map((tier) => (
                      <Badge key={tier.tier} variant="secondary">
                        {tier.tier}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Your receipts</CardTitle>
          <CardDescription>
            Every LLM call you've made — expand a row for the cost-plus receipt
            (provider cost → markup → charged → ceiling authorized).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ActivityStream baseQuery={{ product: 'llm' }} />
        </CardContent>
      </Card>
    </div>
  );
}
